// Phase 3.3 — Procurement list table.
//
// Replaces the MUI <Table> + antd Dropdown/Menu action list in procurement.tsx
// with the shared shadcn DataTable, StatusBadge and a DropdownMenu. Presentation
// only: search + server pagination reuse the screen's handlers, the status/sort
// Select fires the same `getFilterStatusdataList` (the legacy ProcurementSort
// dropdown, which drives `sort`), and every row action calls the screen's
// `procurementListAction(key, item)` with the identical numeric keys.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import { DataTable } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import { Button } from "ui/button";
import { Dialog, DialogContent, DialogTitle } from "ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import { Combobox } from "ui/combobox";
import AppConstant from "../../Globals/AppConstants";

const ALL = "0";

// AWS "type" location code for procurement images — see checkType() in
// awsUpload.ts (case 6 -> "procurement/images/"). Mirrors the constant of
// the same shape in ProcurementFormSheet.tsx.
const PROCUREMENT_IMG_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}procurement/images/`;

const STATUS_TEXT: Record<number, string> = {
  1: "New",
  2: "In Progress",
  3: "Completed",
  4: "On Hold",
};
const statusText = (status: number) => STATUS_TEXT[status] ?? "Not Required";

// Row-action menu — same items/keys as the legacy antd actionOptions().
function menuItemsFor(status: any): { key: string; label: string }[] {
  if (status == 1)
    return [
      { key: "1", label: AppConstant?.edit },
      { key: "2", label: "In Progress" },
      { key: "4", label: "On Hold" },
      { key: "3", label: "Completed" },
      { key: "5", label: "Not Required" },
      { key: "6", label: AppConstant?.Delete },
    ];
  if (status == 2)
    return [
      { key: "1", label: AppConstant?.edit },
      { key: "3", label: "Completed" },
      { key: "4", label: "On Hold" },
      { key: "5", label: "Not Required" },
      { key: "6", label: AppConstant?.Delete },
    ];
  if (status == 3)
    return [
      { key: "1", label: AppConstant?.edit },
      { key: "6", label: AppConstant?.Delete },
    ];
  if (status == 4)
    return [
      { key: "1", label: AppConstant?.edit },
      { key: "2", label: "In Progress" },
      { key: "5", label: "Not Required" },
      { key: "6", label: AppConstant?.Delete },
    ];
  return [{ key: "6", label: AppConstant?.Delete }];
}

// Sort/status options — legacy keys/labels ("0" == All).
const SORT_OPTIONS = [
  { value: "0", label: "All" },
  { value: "1", label: "New" },
  { value: "2", label: "In Progress" },
  { value: "3", label: "Completed" },
  { value: "4", label: "On Hold" },
  { value: "5", label: "Not Required" },
];

export interface ProcurementTableProps {
  data: any[];
  loading: boolean;
  onMenuAction: (key: string, item: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  sort: any;
  onSortChange: (value: any) => void;
  categoryList?: any[];
  categoryId?: any;
  onCategoryFilterChange?: (value: any) => void;
  subCategoryId?: any;
  onSubCategoryFilterChange?: (value: any) => void;
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
  getRowClassName?: (row: any) => string | undefined;
}

export function ProcurementTable({
  data,
  loading,
  onMenuAction,
  searchValue,
  onSearch,
  searchPlaceholder,
  sort,
  onSortChange,
  categoryList,
  categoryId,
  onCategoryFilterChange,
  subCategoryId,
  onSubCategoryFilterChange,
  title,
  headerActions,
  pagination,
  getRowClassName,
}: ProcurementTableProps) {
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);

  // Sub-categories of the currently-selected filter category.
  const subCategoryOptions = React.useMemo(() => {
    const cat = (categoryList || []).find((c: any) => c.categoryId == categoryId);
    return (cat?.subCategories || []).map((s: any) => ({
      subCategoryId: s?.subCategoryId,
      subCategoryName: s?.subCategoryName,
    }));
  }, [categoryList, categoryId]);

  const categoryComboOptions = React.useMemo(
    () => (categoryList || []).map((c: any) => ({ value: String(c?.categoryId), label: c?.categoryName })),
    [categoryList]
  );

  const subCategoryComboOptions = React.useMemo(
    () => subCategoryOptions.map((s: any) => ({ value: String(s?.subCategoryId), label: s?.subCategoryName })),
    [subCategoryOptions]
  );
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "procurementCode", header: "ID", cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.procurementCode || "-"}</span> },
      { id: "categoryName", header: "Category", cell: ({ row }) => row.original?.categoryName },
      { id: "subCategoryName", header: "Sub-Category", cell: ({ row }) => row.original?.subCategoryName },
      { id: "description", header: "Description", cell: ({ row }) => row.original?.description ? row.original.description : "-" },
      { id: "count", header: "Count(Number)", cell: ({ row }) => <span className="tabular-nums">{row.original?.count}</span> },
      { id: "status", header: "Status", cell: ({ row }) => <StatusBadge status={statusText(row.original?.status)} /> },
      {
        id: "attachment",
        header: "Attachment",
        cell: ({ row }) => {
          const imageFileUuid = row.original?.imageFileUuid;
          if (!imageFileUuid) return <span className="text-muted-foreground">-</span>;
          return (
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setPreviewImage(imageFileUuid)}>
              View
            </Button>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => {
          const item = row.original;
          const items = menuItemsFor(item?.status);
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Row actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {items.map((it) => (
                  <DropdownMenuItem key={it.key} onClick={() => onMenuAction(it.key, item)}>
                    {it.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const categorySelect = (
    <Combobox
      className="w-[170px]"
      options={[{ value: ALL, label: "All Categories" }, ...categoryComboOptions]}
      value={categoryId != null ? String(categoryId) : ALL}
      onValueChange={(v) => onCategoryFilterChange?.(v === ALL ? null : v)}
      placeholder="Category"
      searchPlaceholder="Search category..."
    />
  );

  const subCategorySelect = (
    <Combobox
      className="w-[170px]"
      options={[{ value: ALL, label: "All Sub-Categories" }, ...subCategoryComboOptions]}
      value={subCategoryId != null ? String(subCategoryId) : ALL}
      disabled={categoryId == null}
      onValueChange={(v) => onSubCategoryFilterChange?.(v === ALL ? null : v)}
      placeholder="Sub-Category"
      searchPlaceholder="Search sub-category..."
    />
  );

  const sortSelect = (
    <Select value={sort != null ? String(sort) : "0"} onValueChange={(v) => onSortChange(Number(v))}>
      <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
      <SelectContent>
        {SORT_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data || []}
        loading={loading}
        // search field disabled on request
        // searchable
        // searchValue={searchValue}
        // onSearch={onSearch}
        // searchPlaceholder={searchPlaceholder}
        toolbar={
          <div className="flex gap-2">
            {categorySelect}
            {subCategorySelect}
            {sortSelect}
          </div>
        }
        title={title}
        headerActions={headerActions}
        pagination={pagination}
        getRowClassName={getRowClassName}
      />

      <Dialog open={!!previewImage} onOpenChange={(o) => { if (!o) setPreviewImage(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogTitle className="sr-only">Procurement Image</DialogTitle>
          {previewImage && (
            <img
              src={`${PROCUREMENT_IMG_BASE}${previewImage}`}
              alt="Procurement"
              className="max-h-[80vh] w-full rounded-lg object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ProcurementTable;
